/**
 * BlogView — renders a public blog post at a static route. Same shape as
 * LegalView: markdown imported via Vite ?raw, rendered by <Markdown>, minimal
 * chrome (wordmark + back link + Footer). Document title is Korean-first.
 *
 * ADDING A POST = 5 registries, not just one file (2026-08-30 lesson: doing
 * only import+POSTS shipped a soft-404). After editing run
 * `npm run check:blog` and make it pass; it names every missing registry:
 *   1. ./registry.ts: import + POSTS entry
 *   2. client/src/App.tsx: Route path
 *   3. scripts/prerender.mjs: ROUTES + DESCRIPTIONS + BLOG_JSONLD
 *   4. client/public/llms.txt
 *   5. sitemap + content-registry: auto — `node client/scripts/build-sitemap.mjs`
 *      + `npm run seo:registry` regenerate them
 * Real translations additionally go in BLOG_VARIANTS below (+ their
 * /<locale>/blog/<slug> route in prerender.mjs ROUTES/DESCRIPTIONS/BLOG_JSONLD
 * and in build-sitemap.mjs VARIANTS). Single-language posts get NO
 * locale-prefixed URL (docs/plans/seo-signal-fixes.md).
 *
 * ponytail: one component per post (import + entry in POSTS), no slug router
 * until there's a second post to route.
 */
import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { POSTS, BLOG_VARIANTS, WELCOME_I18N, CLUSTER_LOCALES } from './registry'
import { Markdown } from '../Markdown'
import { Footer } from '../landing/Footer'
import { BlogCta } from './BlogCta'
import { LandingI18nProvider } from '../landing/LandingI18n'
import { useLandingT, useLandingLocale, usePublicHref } from '../landing/landing-locale'
import { splitLocalePath } from '../../lib/public-locale'
import { track } from '../../lib/betterstack'


export function BlogView() {
  const { pathname } = useLocation()
  // Normalize trailing slash (Workers serves dist/<route>/index.html as a
  // directory → /blog/x can arrive as /blog/x/). POSTS keys are slash-less.
  // Strip any locale prefix (/ko/blog/…) first — seo plan M2.
  const normalized = splitLocalePath(pathname).path.replace(/\/$/, '') || '/'
  const post = POSTS[normalized]

  if (!post) return null

  // Blog posts with localized versions (per-post variants map)
  const BLOG_I18N = BLOG_VARIANTS[normalized]

  // Same-cluster internal links (cluster mesh — registry.ts), UNSORTED here:
  // BlogViewBody orders them by the resolved reader language. Canonical
  // /blog/<slug>/ URLs even from a locale variant page: that's the indexed,
  // self-canonical address of each post (seo-signal-fixes.md rule 1).
  const relatedAll = post.cluster
    ? Object.entries(POSTS)
        .filter(([key, p]) => p.cluster === post.cluster && key !== normalized)
        .map(([key, p]) => ({ href: `${key}/`, lang: p.lang, title: p.title.replace(/ \| Piccard$/, '') }))
    : []

  return (
    <LandingI18nProvider>
      <BlogViewBody
        post={post}
        localized={normalized === '/welcome' ? WELCOME_I18N : BLOG_I18N}
        relatedAll={relatedAll}
        cluster={post.cluster}
      />
    </LandingI18nProvider>
  )
}

// Heading for the cluster mesh, in the resolved post's language.
const RELATED_I18N: Record<string, string> = {
  ko: '관련 글',
  en: 'Related guides',
  ja: '関連記事',
  'zh-TW': '相關文章',
  zh: '相关文章',
}

// "View the whole topic" link to the cluster hub, in the reader's language.
const CLUSTER_ALL_I18N: Record<string, string> = {
  en: 'All in this topic',
  ko: '이 주제 전체 보기',
  ja: 'このトピックのすべて',
  'zh-TW': '查看整個主題',
  zh: '查看整个主题',
}

function BlogViewBody({
  post,
  localized,
  relatedAll,
  cluster,
}: {
  post: { source: string; lang: string; title: string }
  localized?: Record<string, { source: string; lang: string; title: string }>
  relatedAll?: { href: string; lang: string; title: string }[]
  cluster?: string
}) {
  const t = useLandingT()
  const locale = useLandingLocale()
  const href = usePublicHref()
  const resolved = localized?.[locale] ?? post

  // Reader-language first: a ko post's mesh used to surface en/ja siblings
  // in registry order. Same-language links keep, then everything else.
  const related = (relatedAll ?? [])
    .slice()
    .sort((a, b) => Number(b.lang === resolved.lang) - Number(a.lang === resolved.lang))
    .slice(0, 4)
  // Locale hub when the reader's language has one, else the root hub.
  const hubHref =
    cluster &&
    (CLUSTER_LOCALES[cluster]?.includes(resolved.lang)
      ? `/${resolved.lang}/blog/c/${cluster}`
      : `/blog/c/${cluster}`)

  // /welcome is the extension install page — its ONE job is moving the new
  // user into the app. Before this CTA the page offered only a back-to-home
  // link, so fresh installs had to find their own way in (2026-08-26/27
  // welcome→login wander in the RUM replays). Plain <a> (not <Link>): /app is
  // outside the public router; RequireAuth bounces to /login and returns.
  const isWelcome = localized !== undefined

  useEffect(() => {
    document.title = resolved.title
  }, [resolved])

  return (
    <div className="min-h-screen bg-surface flex flex-col">
      <header className="border-b border-outline-variant">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <a href={href('/')} className="font-headline font-extrabold text-lg text-primary">
            Piccard
          </a>
          <a href={href('/')} className="text-sm text-muted-soft hover:text-on-surface transition-colors">
            {t('landing.blog.backHome')}
          </a>
        </div>
      </header>

      <main className="flex-1 w-full max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-12">
        {isWelcome && (
          <a
            href="/app"
            onClick={() => track('welcome-open-app', {})}
            className="mb-8 flex items-center justify-center gap-2 rounded-full bg-primary px-6 py-3.5 font-bold text-on-primary shadow-card hover:opacity-95 transition-opacity"
          >
            {t('landing.welcome.openApp')} →
          </a>
        )}
        <Markdown source={resolved.source} />
        {!isWelcome && <BlogCta lang={resolved.lang} cluster={cluster} />}
        {(related.length > 0 || hubHref) && (
          <nav className="mt-12 border-t border-outline-variant pt-6" aria-label={RELATED_I18N[resolved.lang] ?? RELATED_I18N.en}>
            <h2 className="font-headline font-bold text-lg mb-3">
              {RELATED_I18N[resolved.lang] ?? RELATED_I18N.en}
            </h2>
            <ul className="space-y-2 list-disc pl-5">
              {related.map((r) => (
                <li key={r.href}>
                  <a href={r.href} className="text-primary hover:underline">
                    {r.title}
                  </a>
                </li>
              ))}
              {hubHref && (
                <li>
                  <a href={`${hubHref}/`} className="text-primary hover:underline font-medium">
                    {CLUSTER_ALL_I18N[resolved.lang] ?? CLUSTER_ALL_I18N.en} →
                  </a>
                </li>
              )}
            </ul>
          </nav>
        )}
      </main>

      <Footer />
    </div>
  )
}
